import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { LoadingState } from '@/components/StateMessage/StateMessage';
import styles from './automations.module.css';

type ReportRow = {
  id: string;
  title: string;
  summary: string | null;
  report_kind: string;
  evidence: unknown;
  created_at: string;
};

type Props = {
  projectId: string;
};

export function AutomationReportsPanel({ projectId }: Props) {
  const [selected, setSelected] = useState<ReportRow | null>(null);

  const reportsQ = useQuery({
    queryKey: ['automation-reports', projectId],
    queryFn: () =>
      apiFetch<{ reports: ReportRow[] }>(`/api/automation-reports?project_id=${projectId}&limit=50`),
  });

  const freshnessQ = useQuery({
    queryKey: ['integration-freshness', projectId],
    queryFn: () =>
      apiFetch<{
        connectors: { connector_type: string; latest_snapshot_date: string; snapshot_count_14d: number }[];
      }>(`/api/dashboard/integration-freshness?project_id=${projectId}`),
  });

  if (reportsQ.isLoading) return <LoadingState />;

  const reports = reportsQ.data?.reports ?? [];
  const connectors = freshnessQ.data?.connectors ?? [];
  const noSnapshots = connectors.length === 0;

  return (
    <div className={styles.workspace}>
      <aside className={styles.listPanel}>
        <h3>Automation reports</h3>
        {reports.length === 0 ? (
          <p>
            Geen reports — configureer generate_analytics_report in een workflow.
            {noSnapshots ? (
              <>
                {' '}
                <strong>Geen GA4/GSC snapshots (14d)</strong> — sync integraties voor betrouwbare KPI&apos;s.
              </>
            ) : (
              <span className={styles.freshnessHint}>
                {' '}
                Snapshots:{' '}
                {connectors
                  .map((c) => `${c.connector_type.toUpperCase()} ${c.snapshot_count_14d}d (${c.latest_snapshot_date.slice(0, 10)})`)
                  .join(' · ')}
              </span>
            )}
          </p>
        ) : null}
        {reports.map((r) => (
          <button
            key={r.id}
            type="button"
            className={[styles.listBtn, selected?.id === r.id ? styles.listBtnActive : ''].join(' ')}
            onClick={() => setSelected(r)}
          >
            {r.title}
            <div style={{ fontSize: '0.75rem', color: 'var(--color-slate)' }}>
              {new Date(r.created_at).toLocaleString('nl-NL')}
            </div>
          </button>
        ))}
      </aside>
      <div className={styles.editorPanel}>
        {selected ? (
          <>
            <h3>{selected.title}</h3>
            <p>
              <code>{selected.report_kind}</code> · {new Date(selected.created_at).toLocaleString('nl-NL')}
            </p>
            {selected.summary ? <pre className={styles.outputPre}>{selected.summary}</pre> : null}
            <h4>Evidence</h4>
            <pre className={styles.outputPre}>{JSON.stringify(selected.evidence, null, 2)}</pre>
          </>
        ) : (
          <p>Selecteer een report.</p>
        )}
      </div>
    </div>
  );
}
